/**
 * One delivery note: preview, print, issue, cancel.
 *
 * THIS IS THE ONLY PLACE A DELIVERY NOTE MOVES STOCK
 * ---------------------------------------------------
 * The issue button below is the single point in the entire application where a
 * delivery note posts a `StockTransaction`, and that is not a convenience — it is
 * the property the whole milestone exists to establish. Draft creation, draft
 * editing, preview and reprint are all read or write paths that leave the ledger
 * untouched, so "the goods left the shop" is true at exactly one moment and can be
 * audited at that moment.
 *
 * WHY THE STOCK WORDING IS THIS LOUD
 * ---------------------------------
 * Every other document screen in this app says issuing it does nothing financial.
 * This one cannot: issuing it deducts stock. A user who issues a delivery note
 * without realising that has made goods disappear from their inventory, and the
 * only trace is a ledger row they did not know to look for. So the button, the
 * status line and the confirmation all say the same thing.
 *
 * IT IS NOT AN INVOICE AND DOES NOT PRETEND TO BE
 * ----------------------------------------------
 * There is no payment history, no balance and no receipt selection here. Those live
 * on the invoice milestone and belong to tax documents; a delivery note that showed
 * a balance would invite a receipt against goods the customer has not been billed
 * for yet.
 */
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { FileDown, Printer, Send, Undo2 } from 'lucide-react';
import { A4_HEIGHT_PX, A4_WIDTH_PX, fitScale, renderPreview } from '@core/doc/preview';
import { printCapabilities, printDocument, saveDocumentPdf } from '@data/services/printService';
import { IssueTransitionError } from '@data/services/issueTransitionService';
import { useIssueDocument } from '@features/documents/useDocuments';
import { toast } from '@ui/components/Toaster';
import { useCancelDeliveryNote, useDeliveryNote } from './useDeliveryNotes';

const assets = new URL('assets/', document.baseURI).href;

export function DeliveryNoteDocumentScreen({ documentId }: { documentId: string }) {
  const query = useDeliveryNote(documentId);
  const issueMutation = useIssueDocument();
  const cancelMutation = useCancelDeliveryNote();
  const capabilities = printCapabilities();
  const [busy, setBusy] = useState(false);
  const scale = fitScale(window.innerWidth * 0.64);

  if (query.isLoading) return <p className="p-4 text-sm text-slate-400">Loading delivery note…</p>;
  if (query.isError || !query.data) {
    return (
      <p role="alert" className="p-4 text-sm text-red-300">
        Could not open this delivery note.
      </p>
    );
  }
  const { model, stored, business, order } = query.data;
  const html = renderPreview(model, business, { scale }, assets);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  async function print() {
    setBusy(true);
    try {
      await printDocument(model, business, assets);
    } catch (error) {
      toast({
        title: 'Could not print delivery note',
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    } finally {
      flushSync(() => setBusy(false));
    }
  }

  async function savePdf() {
    await run(async () => {
      try {
        const result = await saveDocumentPdf(model, business, assets);
        if (result.saved) toast({ title: 'PDF saved', description: result.filePath, tone: 'success' });
      } catch (error) {
        toast({
          title: 'Could not save delivery note PDF',
          description: error instanceof Error ? error.message : 'Try again.',
          tone: 'danger',
        });
      }
    });
  }

  /**
   * Issuing is the stock event, so it is confirmed before it happens.
   *
   * `window.confirm` is the same affordance the draft deletion uses and for the same
   * reason: this is irreversible in the sense that matters — cancelling posts an
   * inverse movement rather than erasing the original, so the ledger keeps both.
   */
  async function issue() {
    const proceed = window.confirm(
      `Issue delivery note ${stored.docNumber}?\n\nThis records that the goods have left the shop and deducts them from stock. It cannot be edited afterwards.`,
    );
    if (!proceed) return;
    await run(async () => {
      try {
        await issueMutation.mutateAsync(documentId);
        await query.refetch();
        toast({
          title: `Delivery note ${stored.docNumber} issued`,
          description: 'Stock has been deducted for the dispatched quantities.',
          tone: 'success',
        });
      } catch (error) {
        toast({
          title: 'Could not issue delivery note',
          description:
            error instanceof IssueTransitionError || error instanceof Error
              ? error.message
              : 'Try again.',
          tone: 'danger',
        });
      }
    });
  }

  async function cancel() {
    const reason = window.prompt(
      `Cancel delivery note ${stored.docNumber}?\n\nThe dispatched quantities will be returned to stock. Give a reason:`,
    );
    if (reason === null) return;
    await run(async () => {
      try {
        const result = await cancelMutation.mutateAsync({
          documentId,
          reason: reason || 'No reason given',
        });
        await query.refetch();
        toast({
          title: `Delivery note ${stored.docNumber} cancelled`,
          description: `${result.reversed} stock movement${result.reversed === 1 ? '' : 's'} reversed.`,
          tone: 'success',
        });
      } catch (error) {
        toast({
          title: 'Could not cancel delivery note',
          description: error instanceof Error ? error.message : 'Try again.',
          tone: 'danger',
        });
      }
    });
  }

  return (
    <div className="flex flex-col items-center gap-3 p-4 text-slate-100">
      <header className="flex w-full max-w-4xl flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-semibold">Delivery Note {stored.docNumber}</h1>
          <p data-testid="delivery-note-status" className="text-xs text-slate-400">
            {stored.status === 'DRAFT'
              ? 'Draft · goods have not moved and no stock has been deducted'
              : stored.status === 'ISSUED'
                ? 'Issued · stock deducted for the dispatched quantities'
                : 'Cancelled · stock returned for the dispatched quantities'}
          </p>
          {order && (
            <p data-testid="delivery-note-source" className="text-xs text-slate-500">
              Dispatch against sales order {order.docNumber} · {order.docDate}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {stored.status === 'DRAFT' && (
            <>
              <a
                className="rounded border border-slate-700 px-3 py-2 text-sm"
                href={`#/sales/deliveries/${documentId}/edit`}
              >
                Edit draft
              </a>
              <button
                data-testid="issue-delivery-note"
                disabled={busy}
                onClick={() => void issue()}
                className="rounded bg-emerald-700 px-3 py-2 text-sm"
              >
                <Send className="mr-1 inline h-4 w-4" />
                Issue &amp; dispatch
              </button>
            </>
          )}
          {stored.status === 'ISSUED' && (
            <button
              data-testid="cancel-delivery-note"
              disabled={busy}
              onClick={() => void cancel()}
              className="rounded border border-red-900 px-3 py-2 text-sm text-red-300"
            >
              <Undo2 className="mr-1 inline h-4 w-4" />
              Cancel &amp; return stock
            </button>
          )}
          <button
            data-testid="print-delivery-note"
            disabled={busy}
            onClick={() => void print()}
            className="rounded bg-brand-500 px-3 py-2 text-sm"
          >
            <Printer className="mr-1 inline h-4 w-4" />
            Print / Reprint
          </button>
          {capabilities.silentPdf && (
            <button
              data-testid="save-pdf-delivery-note"
              disabled={busy}
              onClick={() => void savePdf()}
              className="rounded border border-slate-700 px-3 py-2 text-sm"
            >
              <FileDown className="mr-1 inline h-4 w-4" />
              Save PDF
            </button>
          )}
        </div>
      </header>

      <iframe
        title="Delivery note preview"
        data-testid="delivery-note-preview"
        srcDoc={html}
        style={{ width: A4_WIDTH_PX * scale, height: A4_HEIGHT_PX * scale, border: 0 }}
      />
      <p className="text-xs text-slate-500">
        Preview · issuing this note is what deducts stock · billing is a separate Sales Invoice
      </p>
      <a className="text-sm text-brand-400 underline" href="#/sales/deliveries">
        Back to delivery notes
      </a>
    </div>
  );
}